<template>
  <div>
    <div class="instructionpanelMenu">
      <div v-for="(item, i) in menuItems" :key="item.label">
        <div>
          <a
            class="menu-item"
            :title="item.label"
            :class="{ active: item.isLinkActive }"
            @click="selectActiveItem(i)"
          >
            <b-icon size="is-normal" class="item-icon" :icon="item.icon" />
            <div class="instructionpanelMenu-arrow" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ProjetAnnuelDTO, ProjetAnnuelForInstructionDTO } from "@/model/bean/GeneratedDTOs"
import { NavbarItem } from "@/views/menus/MenuItems"
import { defineComponent } from "vue"
import { LoginService } from "@/services/rest/login/LoginService"
import { BIcon } from "buefy"

export default defineComponent({
  name: "InstructionPanelMenu",
  components: {
    BIcon,
  },
  props: {
    project: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
  },

  emits: ["item-selected"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      menuItems: [] as NavbarItem[],
    }
  },
  watch: {
    project(): void {
      this.refreshMenuItems()
    },
  },
  created(): void {
    this.refreshMenuItems()
  },
  methods: {
    refreshMenuItems(): void {
      const newMenuItems = []
      newMenuItems.push(
        new NavbarItem(
          "contacts",
          this.$t("instruction-panel.contacts.title").toString(),
          "users",
          false,
        ),
      )
      newMenuItems.push(
        new NavbarItem(
          "echanges",
          this.$t("instruction-panel.echanges.title").toString(),
          "mail-bulk",
          false,
        ),
      )

      if (this.project.typeDossier == "DOSSIER_INSCRIPTION") {
        newMenuItems.push(
          new NavbarItem(
            "inscription-evaluation",
            this.$t("instruction-panel.inscription-evaluation.title").toString(),
            "stamp",
            false,
          ),
        )
      } else {
        newMenuItems.push(
          new NavbarItem(
            "labellisation-qualification",
            this.$t("instruction-panel.labellisation-qualification.title").toString(),
            "file",
            false,
          ),
        )

        //Accessible uniquement si cloturé (accepté, refusé ou en devenir)
        if (
          this.project &&
          (this.project.statut === "ACCEPTE" ||
            this.project.statut === "REFUSE" ||
            this.project.statut === "LABELLISATION_EN_DEVENIR")
        ) {
          newMenuItems.push(
            new NavbarItem(
              "labellisation-evaluation-finale",
              this.$t("instruction-panel.labellisation-evaluation-finale.title").toString(),
              "stamp",
              false,
            ),
          )
        }

        //Accessible uniquement si non cloturé (accepté, refusé ou en devenir) et l'utilisateur est instructeur
        if (
          this.isInstructeur() &&
          this.project.statut !== "ACCEPTE" &&
          this.project.statut !== "REFUSE" &&
          this.project.statut !== "LABELLISATION_EN_DEVENIR"
        ) {
          newMenuItems.push(
            new NavbarItem(
              "labellisation-mon-evaluation",
              this.$t("instruction-panel.labellisation-mon-evaluation.title").toString(),
              "check",
              false,
            ),
          )
        }
      }

      let previouslySelectedItemIndex = 0
      for (let i = 0; i < this.menuItems.length; i++) {
        if (this.menuItems[i].isLinkActive && i < newMenuItems.length) {
          previouslySelectedItemIndex = i
        }
      }
      this.menuItems = newMenuItems
      this.selectActiveItem(previouslySelectedItemIndex)
    },
    selectActiveItem(itemIndex: number): void {
      for (let i = 0; i < this.menuItems.length; i++) {
        const isActive = i == itemIndex
        this.menuItems[i].isLinkActive = isActive
        if (isActive) {
          this.$emit("item-selected", this.menuItems[i])
        }
      }
    },
    isInstructeur(): boolean {
      const user = this.loginService.getLoggedUser()

      const projectForInstruction = this.project as ProjetAnnuelForInstructionDTO

      let instructeur = false

      if (projectForInstruction.evaluations !== undefined) {
        projectForInstruction.evaluations.forEach((item) => {
          if (user !== undefined && item.instructeur?.id === user.id) {
            instructeur = true
          }
        })
      }

      return instructeur
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

$active-border-width: 4px;
.instructionpanelMenu {
  width: $main-vertical-menu-width;
  height: calc(100vh - #{$header-height});
  box-shadow: 2px 0 0 0 whitesmoke;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background-color: $instruction-dark;

  .menu-item {
    color: $bookmarkcard-border-color;
    height: $main-vertical-menu-width;
    border-bottom: 1px solid #3c3c3c;
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .icon {
      margin: 0;
      width: $main-vertical-menu-width;
      height: $main-vertical-menu-width;
    }

    &.active {
      background-color: $instruction-light;
      border-right: solid $active-border-width $instruction-selected;

      .icon {
        margin-right: -$active-border-width;
      }

      .instructionpanelMenu-arrow {
        display: inline-block;
        vertical-align: middle;
        width: 0;
        height: 0;
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-left: 10px solid white;
        position: absolute;
      }
    }

    &:hover {
      background-color: $instruction-background;
      color: $main-vertical-menu-bg-color;

      .icon {
        color: $main-vertical-menu-bg-color;
      }

      &.router-link-active {
        border-left: solid $active-border-width white;
      }
    }
  }
}
</style>
